import type { ComponentType } from "react";

/**
 * Map of tool slug -> dynamic widget loader.
 *
 * These MUST stay as `() => import(...)` thunks (never static imports) so the
 * widget modules — which pull in browser-only deps like Excalidraw and touch
 * localStorage/WebGPU — are never evaluated during SSR. LazyWidget invokes the
 * thunk on the client after mount.
 */
export const widgetLoaders: Record<string, () => Promise<{ default: ComponentType }>> = {
  whiteboard: () => import("./Whiteboard"),
  "ai-meeting-notes": () => import("./AiMeetingNotes"),
  "online-notepad": () => import("./OnlineNotepad"),
  "markdown-editor": () => import("./MarkdownEditor"),
  "image-to-text": () => import("./ImageToText"),
  "pdf-to-text": () => import("./PdfToText"),
  "word-counter": () => import("./WordCounter"),
  "soap-note-generator": () => import("./SoapNotes"),
  "mind-map-generator": () => import("./MindMapGenerator"),
  "brain-dump-organizer": () => import("./BrainDumpOrganizer"),
  "eisenhower-matrix": () => import("./EisenhowerMatrix"),
  "swot-analysis": () => import("./SwotAnalysis"),
  "cornell-notes": () => import("./CornellNotes"),
  "flashcard-maker": () => import("./FlashcardMaker"),
  "pomodoro-timer": () => import("./PomodoroTimer"),
  "second-brain-quiz": () => import("./SecondBrainQuiz"),
  "can-i-run-ai": () => import("./CanIRunAi"),
  "canvas-app-finder": () => import("./CanvasAppFinder"),
  "ipad-note-app-finder": () => import("./IpadNoteAppFinder"),
  "audio-transcriber": () => import("./AudioTranscriber"),
  "business-model-canvas": () => import("./BusinessModelCanvas"),
  "concept-map-maker": () => import("./ConceptMapMaker"),
  "customer-journey-map": () => import("./UserJourneyMap"),
  "empathy-map": () => import("./EmpathyMap"),
  "excalidraw-viewer": () => import("./ExcalidrawViewer"),
  "fishbone-diagram": () => import("./Fishbone"),
  "five-whys": () => import("./FiveWhys"),
  "json-canvas-viewer": () => import("./JsonCanvasViewer"),
  "lean-canvas": () => import("./LeanCanvas"),
  "pdf-visual-outline": () => import("./PdfVisualOutline"),
  "retrospective-board": () => import("./RetroBoard"),
  "timeline-maker": () => import("./TimelineMaker"),
  "venn-diagram": () => import("./VennDiagram"),
  "whiteboard-photo-to-canvas": () => import("./WhiteboardPhotoToCanvas"),
  "ai-auto-tagger": () => import("./AutoTagger"),
  "kanban-board": () => import("./KanbanBoard"),
  "org-chart": () => import("./OrgChart"),
  "semantic-note-clusterer": () => import("./SemanticClusterer"),
  "background-remover": () => import("./BackgroundRemover"),
  "handwriting-to-text": () => import("./HandwritingOcr"),
  "opml-to-mind-map": () => import("./OutlineToMindMap"),
  "canvas-to-excalidraw": () => import("./CanvasExcalidrawConverter"),
  "excalidraw-to-png": () => import("./ExcalidrawToPng"),
  "obsidian-canvas-to-image": () => import("./ObsidianCanvasExporter"),
  "xmind-viewer": () => import("./XMindViewer"),
  "drawio-viewer": () => import("./DrawioViewer"),
  "pptx-to-whiteboard": () => import("./PptxToWhiteboard"),
  "enex-viewer": () => import("./EnexViewer"),
  "mermaid-to-excalidraw": () => import("./MermaidToExcalidraw"),
  "mermaid-viewer": () => import("./MermaidViewer"),
  "tldraw-viewer": () => import("./TldrawViewer"),
};
